import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { es } from "date-fns/locale";
import { Check, Users, Calendar as CalendarIcon, ArrowLeft, ArrowRight, Lock, ShieldCheck, BadgeCheck, Sparkles, Info } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import fachadaAsset from "../assets/casa/fachada.webp.asset.json";
import hab1Asset from "../assets/casa/habitacion-1.webp.asset.json";
import hab2Asset from "../assets/casa/habitacion-2.webp.asset.json";
import {
  buscarDisponibilidad,
  iniciarReservaPublica,
  validarCodigoPromocional,
  type ValidacionCodigoPromo,
  type DisponibilidadHabitacion,
} from "@/lib/public-reserva.functions";

export const Route = createFileRoute("/reserva")({
  head: () => ({
    meta: [
      { title: "Reservar · El Pajar de Benito" },
      { name: "description", content: "Reserva online y paga con tarjeta tu escapada a El Pajar de Benito en El Ordial, Guadalajara." },
      { name: "robots", content: "noindex,follow" },
      { property: "og:title", content: "Reservar · El Pajar de Benito" },
      { property: "og:description", content: "Comprueba disponibilidad, calcula el precio y reserva online con pago seguro por tarjeta. Sin intermediarios, sin comisiones." },
      { property: "og:url", content: "/reserva" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/reserva" }],
  }),
  component: Reserva,
});

const fmtEur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(n);

function toISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtFechaES(iso: string) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function imagenHabitacion(h: DisponibilidadHabitacion) {
  return h.nombre.toLowerCase().includes("ordial") ? hab2Asset.url : hab1Asset.url;
}

const inp = "mt-1 w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Field({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`block text-sm text-foreground/85 ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

type Step = 1 | 2 | 3 | 4;
const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: "Fechas" },
  { n: 2, label: "Alojamiento" },
  { n: 3, label: "Tus datos" },
  { n: 4, label: "Pago" },
];

function StepIndicator({ step, onJump, maxReached }: { step: Step; onJump: (s: Step) => void; maxReached: Step }) {
  const pct = ((step - 1) / (STEPS.length - 1)) * 100;
  return (
    <div className="relative">
      {/* Barra de fondo + progreso */}
      <div className="absolute inset-x-4 top-4 h-px bg-border sm:top-5" aria-hidden />
      <div
        className="absolute left-4 top-4 h-px bg-primary transition-all duration-500 sm:top-5"
        style={{ width: `calc((100% - 2rem) * ${pct} / 100)` }}
        aria-hidden
      />
      <ol className="relative flex items-start justify-between">
        {STEPS.map((s) => {
          const active = s.n === step;
          const done = s.n < step;
          const clickable = s.n <= maxReached;
          return (
            <li key={s.n} className="flex min-w-0 flex-1 flex-col items-center">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onJump(s.n)}
                aria-current={active ? "step" : undefined}
                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold transition sm:h-10 sm:w-10 sm:text-sm ${
                  active
                    ? "border-primary bg-primary text-primary-foreground shadow-md shadow-primary/25"
                    : done
                    ? "border-primary bg-primary text-primary-foreground hover:brightness-110"
                    : "border-border bg-background text-muted-foreground"
                } ${clickable && !active ? "cursor-pointer" : ""} disabled:cursor-not-allowed`}
              >
                {done ? <Check className="h-4 w-4" /> : s.n}
              </button>
              <span className={`mt-2 truncate text-[11px] font-medium sm:text-xs ${active ? "text-foreground" : done ? "text-primary" : "text-muted-foreground"}`}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function TrustBadge({ icon: Icon, text }: { icon: typeof ShieldCheck; text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1.5 text-[11px] text-foreground/80">
      <Icon className="h-3.5 w-3.5 text-primary" />
      <span className="truncate">{text}</span>
    </div>
  );
}

function SidePanelInfo() {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-accent">La reserva</p>
      <h3 className="mt-2 font-serif text-xl">Directa, sin comisiones</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Elige tus fechas y en el siguiente paso verás los alojamientos disponibles con su precio final.
      </p>
      <ul className="mt-5 space-y-3 text-sm">
        <li className="flex items-start gap-3"><BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>Confirmación inmediata por email.</span></li>
        <li className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>Pago seguro con tarjeta (Redsys).</span></li>
        <li className="flex items-start gap-3"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>Precio final con IVA, sin sorpresas.</span></li>
      </ul>
      <div className="mt-5 rounded-lg bg-secondary/40 p-3 text-[11px] text-muted-foreground">
        <p><span className="font-medium text-foreground/80">Check-in</span> desde 16:00 · <span className="font-medium text-foreground/80">Check-out</span> hasta 12:00</p>
      </div>
    </div>
  );
}

function Reserva() {
  const buscar = useServerFn(buscarDisponibilidad);
  const iniciar = useServerFn(iniciarReservaPublica);
  const validarPromo = useServerFn(validarCodigoPromocional);

  const [step, setStep] = useState<Step>(1);
  const [maxReached, setMaxReached] = useState<Step>(1);

  const [entrada, setEntrada] = useState("");
  const [salida, setSalida] = useState("");
  const [personas, setPersonas] = useState<number>(2);
  const [resultados, setResultados] = useState<DisponibilidadHabitacion[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [habitacionId, setHabitacionId] = useState<string>("");

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [acepta, setAcepta] = useState(false);

  // Código promocional
  const [codigoPromo, setCodigoPromo] = useState("");
  const [promoAplicado, setPromoAplicado] = useState<Extract<ValidacionCodigoPromo, { valido: true }> | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [validandoPromo, setValidandoPromo] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Validación visible: el usuario ya intentó avanzar desde este paso
  const [showErrors3, setShowErrors3] = useState(false);
  const [touched, setTouched] = useState<{ nombre?: boolean; email?: boolean; telefono?: boolean }>({});

  const [openEntrada, setOpenEntrada] = useState(false);
  const [openSalida, setOpenSalida] = useState(false);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const entradaDate = useMemo(() => {
    if (!entrada) return undefined;
    const [y, m, d] = entrada.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [entrada]);

  const salidaDate = useMemo(() => {
    if (!salida) return undefined;
    const [y, m, d] = salida.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [salida]);

  const minSalida = useMemo(() => {
    if (!entradaDate) return today;
    const d = new Date(entradaDate);
    d.setDate(d.getDate() + 1);
    return d;
  }, [entradaDate, today]);

  const seleccion = useMemo(
    () => resultados?.find((r) => r.id === habitacionId) ?? null,
    [resultados, habitacionId],
  );

  const nochesEstimadas = useMemo(() => {
    if (!entradaDate || !salidaDate) return null;
    const diff = Math.round((salidaDate.getTime() - entradaDate.getTime()) / 86400000);
    return diff > 0 ? diff : null;
  }, [entradaDate, salidaDate]);

  const motivoBuscar = useMemo(() => {
    if (!entrada || !salida) return "Elige las fechas de entrada y salida";
    if (salida <= entrada) return "La salida debe ser posterior a la entrada";
    return null;
  }, [entrada, salida]);

  // Invalidar resultados si cambian fechas/personas
  useEffect(() => {
    setResultados(null);
    setHabitacionId("");
    setMaxReached((m) => (m > 1 ? 1 : m));
    setPromoAplicado(null);
    setPromoError(null);
  }, [entrada, salida, personas]);

  // Si cambia la habitación seleccionada, revalidar/limpiar promo
  useEffect(() => {
    setPromoAplicado(null);
    setPromoError(null);
  }, [habitacionId]);

  const totalBruto = seleccion?.total ?? 0;
  const descuentoPromo = promoAplicado?.descuento ?? 0;
  const totalFinal = Math.max(0, totalBruto - descuentoPromo);

  async function aplicarPromo() {
    if (!seleccion || !codigoPromo.trim()) return;
    setValidandoPromo(true);
    setPromoError(null);
    try {
      const r = await validarPromo({
        data: {
          codigo: codigoPromo.trim(),
          fecha_entrada: entrada,
          fecha_salida: salida,
          precio: totalBruto,
        },
      });
      if (r.valido) {
        setPromoAplicado(r);
      } else {
        setPromoAplicado(null);
        setPromoError(r.error);
      }
    } catch (e) {
      setPromoError((e as Error).message);
    } finally {
      setValidandoPromo(false);
    }
  }

  function quitarPromo() {
    setPromoAplicado(null);
    setCodigoPromo("");
    setPromoError(null);
  }

  function goTo(s: Step) {
    setError(null);
    setStep(s);
    setMaxReached((m) => (s > m ? s : m));
  }

  async function handleBuscar() {
    if (motivoBuscar) { setError(motivoBuscar); return; }
    setError(null);
    setBuscando(true);
    setResultados(null);
    setHabitacionId("");
    try {
      const r = await buscar({ data: { fecha_entrada: entrada, fecha_salida: salida, num_huespedes: personas } });
      setResultados(r);
      goTo(2);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBuscando(false);
    }
  }

  const motivoDatos = useMemo(() => {
    if (nombre.trim().length < 2) return "Indica tu nombre";
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Indica un email válido";
    if (telefono.trim().length < 6) return "Indica un teléfono";
    if (!acepta) return "Acepta las condiciones para continuar";
    return null;
  }, [nombre, email, telefono, acepta]);

  const errores3 = useMemo(() => ({
    nombre: nombre.trim().length < 2 ? "Introduce tu nombre" : null,
    email: !/^\S+@\S+\.\S+$/.test(email) ? "Introduce un email válido" : null,
    telefono: telefono.trim().length < 6 ? "Introduce un teléfono de contacto" : null,
    acepta: !acepta ? "Debes aceptar las condiciones para continuar" : null,
  }), [nombre, email, telefono, acepta]);

  function intentarPaso3() {
    setShowErrors3(true);
    if (motivoDatos) {
      setError(motivoDatos);
      const firstId = errores3.nombre ? "f-nombre" : errores3.email ? "f-email" : errores3.telefono ? "f-telefono" : errores3.acepta ? "f-acepta" : null;
      if (firstId) {
        const el = document.getElementById(firstId);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); (el as HTMLInputElement).focus?.(); }
      }
      return;
    }
    setError(null);
    goTo(4);
  }

  const motivoNoPagar = useMemo(() => {
    if (!habitacionId || !seleccion) return "Elige una habitación";
    if (!seleccion.disponible || seleccion.total <= 0) return "Sin disponibilidad";
    return motivoDatos;
  }, [habitacionId, seleccion, motivoDatos]);

  async function pagar() {
    if (motivoNoPagar) { setError(motivoNoPagar); return; }
    setError(null);
    setEnviando(true);
    try {
      const payload = await iniciar({
        data: {
          habitacion_id: habitacionId,
          fecha_entrada: entrada,
          fecha_salida: salida,
          num_huespedes: personas,
          nombre: nombre.trim(),
          email: email.trim(),
          telefono: telefono.trim(),
          mensaje: mensaje.trim(),
          acepta_condiciones: acepta,
          codigo_promocional: promoAplicado ? promoAplicado.codigo : "",
        },
      });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = payload.url;
      for (const [k, v] of Object.entries({
        Ds_SignatureVersion: payload.Ds_SignatureVersion,
        Ds_MerchantParameters: payload.Ds_MerchantParameters,
        Ds_Signature: payload.Ds_Signature,
      })) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setError((e as Error).message);
      setEnviando(false);
    }
  }

  function Resumen({ compact }: { compact?: boolean }) {
    return (
      <div className={compact ? "" : "rounded-2xl border border-border bg-card p-5 shadow-sm"}>
        {!compact && <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-accent">Resumen</p>}
        {seleccion ? (
          <p className={compact ? "text-sm font-medium" : "mt-2 font-serif text-lg"}>{seleccion.nombre}</p>
        ) : (
          <p className={compact ? "text-sm text-muted-foreground" : "mt-2 text-sm text-muted-foreground"}>Sin alojamiento</p>
        )}
        <dl className={`${compact ? "mt-1" : "mt-4"} space-y-1.5 text-sm`}>
          <div className="flex justify-between"><dt className="text-muted-foreground">Entrada</dt><dd>{fmtFechaES(entrada)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Salida</dt><dd>{fmtFechaES(salida)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Personas</dt><dd>{personas}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Noches</dt><dd>{seleccion?.noches ?? nochesEstimadas ?? "—"}</dd></div>
          {promoAplicado && seleccion && (
            <>
              <div className="flex justify-between"><dt className="text-muted-foreground">Subtotal</dt><dd>{fmtEur(totalBruto)}</dd></div>
              <div className="flex justify-between text-emerald-700"><dt>Descuento ({promoAplicado.codigo})</dt><dd>−{fmtEur(descuentoPromo)}</dd></div>
            </>
          )}
        </dl>
        <div className={`${compact ? "mt-3" : "mt-4"} border-t border-border pt-3`}>
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Total</span>
            <span className="font-serif text-2xl">{seleccion ? fmtEur(totalFinal) : "—"}</span>
          </div>
          <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> IVA incluido · Pago seguro Redsys</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-border bg-secondary/30">
        <img
          src={fachadaAsset.url}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 pt-24 pb-10 md:px-6 md:pt-28 md:pb-14">
          <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Reservar" }]} />
          <p className="mt-4 text-xs uppercase tracking-[0.28em] text-accent">Reservar</p>
          <h1 className="mt-2 max-w-2xl font-serif text-4xl text-foreground md:text-5xl">Reserva tu escapada</h1>
          <p className="mt-4 max-w-xl text-muted-foreground">
            Te guiamos paso a paso. Precio final, confirmación inmediata y pago 100% seguro.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <TrustBadge icon={BadgeCheck} text="Confirmación inmediata" />
            <TrustBadge icon={ShieldCheck} text="Pago seguro Redsys" />
            <TrustBadge icon={Sparkles} text="Sin comisiones" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 pb-40 md:px-6 md:py-14 md:pb-20">
        {/* Stepper */}
        <div className="mx-auto max-w-2xl">
          <StepIndicator step={step} onJump={(s) => goTo(s)} maxReached={maxReached} />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm md:p-8">
            <div className="space-y-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-serif text-2xl">¿Cuándo quieres venir?</h2>
                <p className="mt-1 text-sm text-muted-foreground">Elige día de entrada, día de salida y cuántas personas seréis.</p>
              </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Entrada</p>
                <Popover open={openEntrada} onOpenChange={setOpenEntrada}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="mt-1 h-11 w-full justify-start rounded-lg font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{entrada ? fmtFechaES(entrada) : <span className="text-muted-foreground">Elige fecha</span>}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={es}
                      weekStartsOn={1}
                      selected={entradaDate}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = toISO(d);
                        setEntrada(iso);
                        if (salida && salida <= iso) setSalida("");
                        setOpenEntrada(false);
                      }}
                      disabled={{ before: today }}
                      defaultMonth={entradaDate ?? today}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Salida</p>
                <Popover open={openSalida} onOpenChange={setOpenSalida}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      disabled={!entrada}
                      className="mt-1 h-11 w-full justify-start rounded-lg font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {salida
                          ? fmtFechaES(salida)
                          : <span className="text-muted-foreground">Elige fecha</span>}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={es}
                      weekStartsOn={1}
                      selected={salidaDate}
                      onSelect={(d) => {
                        if (!d) return;
                        setSalida(toISO(d));
                        setOpenSalida(false);
                      }}
                      disabled={{ before: minSalida }}
                      defaultMonth={salidaDate ?? minSalida}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Personas</p>
                <div className="relative mt-1">
                  <Users className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="number" min={1} max={20}
                    value={personas}
                    onChange={(e) => setPersonas(Number(e.target.value) || 1)}
                    className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>
              {motivoBuscar && (
                <p className="flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-2 text-xs text-muted-foreground"><Info className="h-3.5 w-3.5" /> {motivoBuscar}</p>
              )}
              {!motivoBuscar && (
                <p className="flex items-center gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs text-primary"><Check className="h-3.5 w-3.5" /> Perfecto. Ya puedes ver los alojamientos disponibles.</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-serif text-2xl">Elige alojamiento para continuar</h2>
                <p className="mt-1 text-sm text-muted-foreground">Estas son las opciones para {fmtFechaES(entrada)} → {fmtFechaES(salida)}, {personas} {personas === 1 ? "persona" : "personas"}.</p>
              </div>
              {buscando && <p className="text-sm text-muted-foreground">Buscando disponibilidad…</p>}
              {resultados && resultados.length === 0 && (
                <p className="rounded-md border border-border bg-secondary/30 p-4 text-sm text-muted-foreground">No hay alojamientos para esas fechas. Vuelve atrás y prueba con otras.</p>
              )}
              {resultados && resultados.length > 0 && (
                <div className="space-y-3">
                {resultados.map((h) => {
                  const selected = habitacionId === h.id;
                  const noDispo = !h.disponible;
                  return (
                    <button
                      key={h.id}
                      type="button"
                      disabled={noDispo}
                      onClick={() => { setHabitacionId(h.id); goTo(3); }}
                      className={`group relative grid w-full grid-cols-[96px_minmax(0,1fr)] gap-3 overflow-hidden rounded-md border text-left transition sm:grid-cols-[150px_minmax(0,1fr)_auto] sm:gap-4 ${selected ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:border-primary/50"} ${noDispo ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      <img src={imagenHabitacion(h)} alt={h.nombre} loading="lazy" className="h-full w-full object-cover" />
                      <div className="min-w-0 py-3 pr-3 sm:pr-2">
                        <p className="truncate font-medium">{h.nombre}</p>
                        {h.descripcion && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{h.descripcion}</p>}
                        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {h.capacidad && (
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />Hasta {h.capacidad}</span>
                          )}
                          {h.noches > 0 && <span>· {h.noches} {h.noches === 1 ? "noche" : "noches"}</span>}
                        </p>
                        {h.motivo && <p className="mt-1 text-xs font-medium text-destructive">{h.motivo}</p>}
                        {!h.motivo && h.desglose.length > 0 && h.desglose.length <= 7 && (
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {h.desglose.map((n) => `${new Date(n.fecha + "T12:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric" })} ${Math.round(n.precio)} €`).join(" · ")}
                          </p>
                        )}
                      </div>
                      <div className="col-span-2 flex items-center justify-between gap-3 border-t border-border px-3 py-3 text-right sm:col-span-1 sm:flex-col sm:items-end sm:justify-center sm:border-t-0">
                        {noDispo ? (
                          <span className="rounded-full bg-muted px-3 py-1 text-[11px] uppercase tracking-wider text-muted-foreground">{h.motivo?.startsWith("Mínimo") ? "Estancia mínima" : "No disponible"}</span>
                        ) : (
                          <>
                            <span className="text-left sm:text-right">
                              <span className="block font-serif text-xl">{fmtEur(h.total)}</span>
                              {h.noches > 0 && <span className="block text-[11px] text-muted-foreground">{fmtEur(h.precio_noche)}/noche</span>}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
                              {selected ? <Check className="h-3 w-3" /> : null} Seleccionar <ArrowRight className="h-3 w-3" />
                            </span>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-serif text-2xl">Tus datos</h2>
                <p className="mt-1 text-sm text-muted-foreground">Necesitamos esta información para confirmar tu reserva.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nombre completo" wide>
                  <input
                    id="f-nombre"
                    className={`${inp} ${(showErrors3 || touched.nombre) && errores3.nombre ? "border-destructive" : ""}`}
                    aria-invalid={!!((showErrors3 || touched.nombre) && errores3.nombre)}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, nombre: true }))}
                  />
                  {(showErrors3 || touched.nombre) && errores3.nombre && (
                    <p className="mt-1 text-xs text-destructive">{errores3.nombre}</p>
                  )}
                </Field>
                <Field label="Email">
                  <input
                    id="f-email"
                    type="email"
                    className={`${inp} ${(showErrors3 || touched.email) && errores3.email ? "border-destructive" : ""}`}
                    aria-invalid={!!((showErrors3 || touched.email) && errores3.email)}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  />
                  {(showErrors3 || touched.email) && errores3.email && (
                    <p className="mt-1 text-xs text-destructive">{errores3.email}</p>
                  )}
                </Field>
                <Field label="Teléfono">
                  <input
                    id="f-telefono"
                    className={`${inp} ${(showErrors3 || touched.telefono) && errores3.telefono ? "border-destructive" : ""}`}
                    aria-invalid={!!((showErrors3 || touched.telefono) && errores3.telefono)}
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, telefono: true }))}
                  />
                  {(showErrors3 || touched.telefono) && errores3.telefono && (
                    <p className="mt-1 text-xs text-destructive">{errores3.telefono}</p>
                  )}
                </Field>
                <Field label="Mensaje (opcional)" wide><textarea rows={3} className={inp} value={mensaje} onChange={(e) => setMensaje(e.target.value)} /></Field>
              </div>
              <div>
                <label className="flex items-start gap-2 text-sm">
                  <input id="f-acepta" type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} className="mt-1" />
                  <span>
                    He leído y acepto las{" "}
                    <a href="/legal/condiciones" target="_blank" rel="noopener" className="underline hover:text-primary">condiciones de reserva</a>{" "}
                    y la{" "}
                    <a href="/legal/privacidad" target="_blank" rel="noopener" className="underline hover:text-primary">política de privacidad</a>.
                  </span>
                </label>
                {showErrors3 && errores3.acepta && (
                  <p className="mt-1 text-xs text-destructive">{errores3.acepta}</p>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-serif text-2xl">Confirma y paga</h2>
                <p className="mt-1 text-sm text-muted-foreground">Revisa los datos antes de pasar a la pasarela segura.</p>
              </div>
              <div className="rounded-md border border-border bg-background p-4 text-sm">
                <p className="font-medium">{seleccion?.nombre}</p>
                <p className="mt-1 text-muted-foreground">{fmtFechaES(entrada)} → {fmtFechaES(salida)} · {personas} {personas === 1 ? "persona" : "personas"} · {seleccion?.noches} {seleccion?.noches === 1 ? "noche" : "noches"}</p>
                <div className="mt-3 border-t pt-3 text-foreground/85">
                  <p>{nombre}</p>
                  <p className="text-muted-foreground">{email} · {telefono}</p>
                  {mensaje && <p className="mt-2 whitespace-pre-line text-muted-foreground">“{mensaje}”</p>}
                </div>
                <div className="mt-3 border-t pt-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Código promocional</p>
                  {promoAplicado ? (
                    <div className="flex items-center justify-between gap-3 rounded-md bg-emerald-50 px-3 py-2 text-sm">
                      <div>
                        <p className="font-medium text-emerald-800">{promoAplicado.codigo}</p>
                        <p className="text-xs text-emerald-700">Descuento aplicado: −{fmtEur(descuentoPromo)}</p>
                      </div>
                      <button type="button" onClick={quitarPromo} className="text-xs text-emerald-800 underline">Quitar</button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex gap-2">
                        <input
                          className={inp}
                          placeholder="Introduce tu código"
                          value={codigoPromo}
                          onChange={(e) => setCodigoPromo(e.target.value.toUpperCase())}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aplicarPromo(); } }}
                        />
                        <button
                          type="button"
                          onClick={aplicarPromo}
                          disabled={validandoPromo || !codigoPromo.trim() || !seleccion}
                          className="shrink-0 rounded-sm border border-border px-3 py-2 text-sm hover:bg-secondary disabled:opacity-50"
                        >
                          {validandoPromo ? "…" : "Aplicar"}
                        </button>
                      </div>
                      {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-baseline justify-between border-t pt-3">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-serif text-2xl">{seleccion ? fmtEur(totalFinal) : "—"}</span>
                </div>
              </div>
              <p className="flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> Pago seguro Redsys · IVA incluido</p>
            </div>
          )}

          {error && <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}

          {/* Navegación pasos — desktop */}
          <div className="hidden items-center justify-between gap-3 border-t pt-6 lg:flex">
            {step > 1 ? (
              <button type="button" onClick={() => goTo((step - 1) as Step)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> Atrás
              </button>
            ) : <span />}
            {step === 1 && (
              <button type="button" onClick={handleBuscar} disabled={!!motivoBuscar || buscando} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {buscando ? "Comprobando disponibilidad…" : "Ver alojamientos disponibles"} <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 2 && seleccion && (
              <button type="button" onClick={() => goTo(3)} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                Siguiente <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 3 && (
              <button type="button" onClick={intentarPaso3} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                Revisar y pagar <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 4 && (
              <button type="button" onClick={pagar} disabled={enviando || !!motivoNoPagar} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                <Lock className="h-4 w-4" /> {enviando ? "Redirigiendo…" : seleccion?.total ? `Pagar ${fmtEur(totalFinal)}` : "Pagar con tarjeta"}
              </button>
            )}
          </div>

          <div className="border-t pt-6 text-sm text-muted-foreground">
            ¿Tienes dudas antes de reservar? Escríbenos por{" "}
            <a className="underline" href="https://wa.me/34641696242" target="_blank" rel="noopener">WhatsApp</a>
            {" o "}
            <a className="underline" href="mailto:hola@elpajardebenito.es">email</a>.
          </div>
            </div>
          </div>

          <aside className="hidden h-fit lg:sticky lg:top-24 lg:block">
            {step >= 2 ? <Resumen /> : <SidePanelInfo />}
          </aside>
        </div>

      {/* Barra fija móvil */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur lg:hidden">
        <div className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          {step > 1 ? (
            <button type="button" onClick={() => goTo((step - 1) as Step)} className="inline-flex w-fit items-center gap-1 rounded-full border border-border px-3 py-2 text-xs text-muted-foreground">
              <ArrowLeft className="h-3 w-3" /> Atrás
            </button>
          ) : <span className="min-w-0 truncate text-[11px] text-muted-foreground">Paso {step} de 4</span>}
          {step === 1 && (
            <button type="button" onClick={handleBuscar} disabled={!!motivoBuscar || buscando} className="inline-flex max-w-[min(72vw,22rem)] items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-50">
              <span className="truncate">{buscando ? "Comprobando…" : "Ver alojamientos"}</span> <ArrowRight className="h-4 w-4" />
            </button>
          )}
          {step === 2 && (
            <button type="button" onClick={() => seleccion && goTo(3)} disabled={!seleccion} className="inline-flex max-w-[min(72vw,22rem)] items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-50">
              Siguiente <ArrowRight className="h-4 w-4" />
            </button>
          )}
          {step === 3 && (
            <button type="button" onClick={intentarPaso3} className="inline-flex max-w-[min(72vw,22rem)] items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground">
              <span className="truncate">Revisar y pagar</span> <ArrowRight className="h-4 w-4" />
            </button>
          )}
          {step === 4 && (
            <button type="button" onClick={pagar} disabled={enviando || !!motivoNoPagar} className="inline-flex max-w-[min(72vw,22rem)] items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-50">
              <Lock className="h-4 w-4" /> {enviando ? "…" : seleccion ? `Pagar ${fmtEur(totalFinal)}` : "Pagar"}
            </button>
          )}
        </div>
      </div>
      </section>
      {/* Espacio para que el footer no quede bajo la barra fija en móvil */}
      <div aria-hidden className="h-24 lg:hidden" />
    </>
  );
}